/**
 * useConferenciaLacresMatching - Hook for matching contagem items with conferencia lacres
 *
 * Epic 46/48: Match by cliente + regime + codigo + lote + validade to populate "Coletado" column
 * Returns aggregated contado values for each matching key.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import apiClient from '../services/apiClient';

// Item from conferencia lacres API
export interface ItemConferenciaLacre {
  id: number;
  cliente: string;
  regime: string;  // "SIM" | "NÃO"
  codigo: string;
  lote: string;
  validade: string;
  contado: number;
  coletar: number;
  diferenca: number;
}

// Result of matching a contagem item with conferencia lacres
export interface ConferenciaLacreMatch {
  totalContado: number;
}

// Interface for items that can be matched
export interface MatchableItemLacre {
  cliente?: string;
  regime?: string;  // "Com Regime" | "Sem Regime" | "-"
  produto: string;  // codigo_produto
  lote: string;
  validade: string;
}

export interface UseConferenciaLacresMatchingReturn {
  items: ItemConferenciaLacre[];
  matchMap: Map<string, ConferenciaLacreMatch>;
  getMatchForItem: (item: MatchableItemLacre) => ConferenciaLacreMatch | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Normalize regime value for matching
 * "Com Regime" → "SIM"
 * "Sem Regime" → "NÃO"
 */
const normalizeRegime = (regime: string | undefined): string => {
  if (!regime || regime === '-') return '';
  if (regime === 'Com Regime' || regime === 'SIM' || regime === 'Sim') return 'SIM';
  if (regime === 'Sem Regime' || regime === 'NÃO' || regime === 'Não') return 'NÃO';
  return regime.toUpperCase();
};

/**
 * Normalize date to DD/MM/YYYY format for matching
 * Handles both YYYY-MM-DD (API) and DD/MM/YYYY (UI) formats
 */
const normalizeValidade = (validade: string | undefined): string => {
  if (!validade) return '';

  // Already in DD/MM/YYYY format
  if (validade.includes('/')) {
    return validade;
  }

  // Convert from YYYY-MM-DD to DD/MM/YYYY
  if (validade.includes('-')) {
    const [year, month, day] = validade.split('-');
    return `${day}/${month}/${year}`;
  }

  return validade;
};

/**
 * Build matching key from item properties
 * Format: "cliente|regime|codigo|lote|validade"
 */
const buildMatchKey = (
  cliente: string,
  regime: string,
  codigo: string,
  lote: string,
  validade: string
): string => {
  const normalizedRegime = normalizeRegime(regime);
  const normalizedValidade = normalizeValidade(validade);
  return `${cliente || ''}|${normalizedRegime}|${codigo}|${lote}|${normalizedValidade}`;
};

/**
 * Hook to fetch and match conferencia lacres with contagem items
 */
export function useConferenciaLacresMatching(jobId: number): UseConferenciaLacresMatchingReturn {
  const [items, setItems] = useState<ItemConferenciaLacre[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all conferencia lacres items for the job
  const fetchItems = useCallback(async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get(`/jobs/${jobId}/conferencia-lacres/items`);
      setItems(response.data.itens || []);
    } catch (err: any) {
      console.error('Error fetching conferencia lacres for matching:', err);
      setError(err.response?.data?.detail || err.message || 'Erro ao carregar conferência de lacres');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  // Load items on mount and when jobId changes
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Build match map from conferencia lacres items
  // Key: "cliente|regime|codigo|lote|validade"
  // Value: { totalContado }
  const matchMap = useMemo(() => {
    const map = new Map<string, ConferenciaLacreMatch>();

    items.forEach((item) => {
      const key = buildMatchKey(
        item.cliente,
        item.regime,
        item.codigo,
        item.lote,
        item.validade
      );

      const existing = map.get(key);
      const contado = item.contado || 0;

      if (existing) {
        existing.totalContado += contado;
      } else {
        map.set(key, {
          totalContado: contado,
        });
      }
    });

    return map;
  }, [items]);

  // Get match for a specific contagem item
  const getMatchForItem = useCallback(
    (item: MatchableItemLacre): ConferenciaLacreMatch | null => {
      if (!item.produto || !item.lote) {
        return null;
      }

      // Match by cliente + regime + codigo + lote + validade
      const key = buildMatchKey(
        item.cliente || '',
        item.regime || '',
        item.produto,
        item.lote,
        item.validade
      );

      return matchMap.get(key) || null;
    },
    [matchMap]
  );

  return {
    items,
    matchMap,
    getMatchForItem,
    loading,
    error,
    refetch: fetchItems,
  };
}

export default useConferenciaLacresMatching;
